<template>
  <main class="support-page">
    <section class="support-shell">
      <div v-if="submitSuccess" class="support-success" role="status">
        <span class="material-symbols-outlined success-icon" aria-hidden="true">check_circle</span>
        <h1>Ticket Submitted</h1>
        <p>
          Ticket <strong>#{{ ticketNumber }}</strong> was sent to SubAngstrom support.
        </p>
        <div v-if="accessUrl" class="access-link">
          <span>Access link</span>
          <code>{{ accessUrl }}</code>
          <button type="button" @click="copyAccessUrl">
            {{ copied ? "Copied" : "Copy" }}
          </button>
        </div>
        <div class="support-actions">
          <router-link to="/home" class="secondary-action">Back to Home</router-link>
          <button type="button" class="primary-action" @click="resetForm">
            Submit Another Ticket
          </button>
        </div>
      </div>

      <form v-else class="support-form" @submit.prevent="submitTicket">
        <header class="support-header">
          <div>
            <p>SubAngstrom</p>
            <h1>Customer Support</h1>
          </div>
          <a class="support-email" href="mailto:support@subangstrom.com">
            support@subangstrom.com
          </a>
        </header>

        <div class="form-grid">
          <label class="field field-wide" for="support-subject">
            <span>Subject</span>
            <input
              id="support-subject"
              v-model.trim="form.subject"
              type="text"
              required
              placeholder="Brief summary"
            />
          </label>

          <label class="field" for="support-type">
            <span>Type</span>
            <select id="support-type" v-model="form.type" required>
              <option value="BUG">Bug Report</option>
              <option value="FEATURE_REQUEST">Feature Request</option>
              <option value="QUESTION">Question</option>
              <option value="FEEDBACK">Feedback</option>
              <option value="OTHER">Other</option>
            </select>
          </label>

          <label class="field" for="support-email">
            <span>Email</span>
            <input
              id="support-email"
              v-model.trim="form.contactEmail"
              type="email"
              required
              placeholder="name@lab.org"
            />
          </label>

          <label class="field" for="support-name">
            <span>Name</span>
            <input
              id="support-name"
              v-model.trim="form.contactName"
              type="text"
              placeholder="Your name"
            />
          </label>

          <label class="field field-wide" for="support-description">
            <span>Description</span>
            <textarea
              id="support-description"
              v-model.trim="form.description"
              required
              rows="8"
              placeholder="Include the page, expected result, actual result, and any relevant sample or inventory identifiers."
            ></textarea>
          </label>
        </div>

        <p v-if="errorMessage" class="error-message" role="alert">
          {{ errorMessage }}
        </p>

        <footer class="support-actions">
          <router-link to="/home" class="secondary-action">Cancel</router-link>
          <button type="submit" class="primary-action" :disabled="isSubmitting">
            {{ isSubmitting ? "Submitting..." : "Submit Ticket" }}
          </button>
        </footer>
      </form>
    </section>
  </main>
</template>

<script>
const CLOUD_API_URL =
  import.meta.env.VITE_CLOUD_API_URL || "https://activation.subangstrom.com";

export default {
  data() {
    return {
      form: {
        subject: "",
        type: "BUG",
        description: "",
        contactEmail: "",
        contactName: "",
      },
      isSubmitting: false,
      errorMessage: "",
      submitSuccess: false,
      ticketNumber: "",
      accessToken: "",
      copied: false,
    };
  },
  computed: {
    accessUrl() {
      if (!this.accessToken) return "";
      return `${CLOUD_API_URL}/api/v1/tickets/lookup?token=${this.accessToken}`;
    },
  },
  mounted() {
    const user = this.appStore.state.activeUser || {};
    this.form.contactEmail = user.email || "";
    this.form.contactName =
      user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim();
  },
  methods: {
    async submitTicket() {
      this.errorMessage = "";
      this.isSubmitting = true;

      const formData = new FormData();
      formData.append("subject", this.form.subject);
      formData.append("type", this.form.type);
      formData.append("description", this.form.description);
      formData.append("contactEmail", this.form.contactEmail);
      formData.append("appVersion", this.$version || "unknown");

      if (this.form.contactName) {
        formData.append("contactName", this.form.contactName);
      }

      try {
        const response = await fetch(`${CLOUD_API_URL}/api/v1/tickets`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || "Failed to submit ticket");
        }

        this.ticketNumber = data.data?.ticketNumber || "pending";
        this.accessToken = data.data?.accessToken || "";
        this.submitSuccess = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        this.errorMessage =
          error.message || "Network error. Please email support@subangstrom.com.";
      } finally {
        this.isSubmitting = false;
      }
    },
    async copyAccessUrl() {
      if (!this.accessUrl) return;
      await navigator.clipboard.writeText(this.accessUrl);
      this.copied = true;
      window.setTimeout(() => {
        this.copied = false;
      }, 1600);
    },
    resetForm() {
      this.form.subject = "";
      this.form.type = "BUG";
      this.form.description = "";
      this.errorMessage = "";
      this.submitSuccess = false;
      this.ticketNumber = "";
      this.accessToken = "";
    },
  },
};
</script>

<style scoped>
.support-page {
  min-height: calc(100vh - 24px);
  padding: 18px;
  background: #fff;
  color: #172033;
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.support-shell {
  width: min(860px, 100%);
  margin: 0 auto;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgb(15 23 42 / 0.07);
}

.support-form,
.support-success {
  padding: 22px;
}

.support-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5edf6;
}

.support-header p,
.support-header h1 {
  margin: 0;
}

.support-header p {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.support-header h1,
.support-success h1 {
  color: rgb(5, 46, 77);
  font-size: 26px;
  font-weight: 900;
}

.support-email {
  color: #335c8b;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
}

.support-email:hover {
  text-decoration: underline;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.field {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.field-wide {
  grid-column: 1 / -1;
}

.field span {
  color: #405065;
  font-size: 12px;
  font-weight: 900;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  min-width: 0;
  border: 1px solid #c9d5e5;
  border-radius: 7px;
  background: #fff;
  color: #172033;
  font: inherit;
  font-size: 14px;
}

.field input,
.field select {
  min-height: 39px;
  padding: 0 10px;
}

.field textarea {
  padding: 10px;
  resize: vertical;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: #6889b3;
  outline: 0;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.12);
}

.error-message {
  margin: 14px 0 0;
  padding: 10px 12px;
  border: 1px solid #fecaca;
  border-radius: 7px;
  background: #fff1f2;
  color: #b42318;
  font-size: 13px;
  font-weight: 800;
}

.support-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.primary-action,
.secondary-action,
.access-link button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 7px;
  font: inherit;
  font-size: 13px;
  font-weight: 900;
  text-decoration: none;
  cursor: pointer;
}

.primary-action {
  border: 1px solid #2f537d;
  background: #335c8b;
  color: #fff;
}

.primary-action:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.secondary-action,
.access-link button {
  border: 1px solid #d5dfec;
  background: #f8fafc;
  color: #335c8b;
}

.support-success {
  text-align: center;
}

.success-icon {
  color: #15803d;
  font-size: 54px;
}

.support-success p {
  margin: 8px 0 0;
  color: #526173;
  font-size: 15px;
  font-weight: 700;
}

.access-link {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 10px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fbff;
  text-align: left;
}

.access-link span {
  color: #405065;
  font-size: 12px;
  font-weight: 900;
}

.access-link code {
  overflow: hidden;
  color: #526173;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 700px) {
  .support-page {
    padding: 10px;
  }

  .support-header,
  .support-actions {
    flex-direction: column;
  }

  .form-grid,
  .access-link {
    grid-template-columns: 1fr;
  }
}
</style>
